/**
 * EventsManagementView
 * Vista de gestión de eventos con tabla, filtros y acciones CRUD
 * Sprint 21 - Phase 2
 */

import { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Progress } from '../ui/progress';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../ui/table';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../ui/select';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import {
    Plus,
    Calendar,
    Pencil,
    Power,
    ChevronLeft,
    ChevronRight,
    Loader2,
    Search,
    ArrowUpDown,
    ArrowUp,
    ArrowDown,
    ListTree,
} from 'lucide-react';
import api from '../../src/api';
import { settingsApi } from '../../src/api/settings';
import { toast } from 'sonner';
import { useAuth } from '../auth-context';
import { PERMISSIONS, hasPermission } from '../../src/hooks/usePermission';
import { ALLOW_FREE_OYENTE_UNACH_SETTING_KEY } from '../../../shared/config-whitelist';
import { SubeventsManager } from './SubeventsManager';

// Types
interface Edition {
    id: string;
    year: number;
    name: string | null;
    isActive: boolean;
}

interface Event {
    id: string;
    editionId: string;
    slug: string;
    name: string;
    shortName: string | null;
    axis: string | null;
    description: string | null;
    syswebCode?: string | null;
    syswebUrl?: string | null;
    contactEmail?: string | null;
    contactPhone?: string | null;
    maxCapacity: number;
    currentCount: number;
    attendeeCost: number;
    speakerCost: number;
    isActive: boolean;
    attendeeRegStart: string | null;
    attendeeRegEnd: string | null;
    speakerBraceletEnabled: boolean;
}

interface EventAdminDetail extends Event {
    syswebId: string | null;
    allowFreeOyenteUnach: boolean;
}

interface EventFormValues {
    name: string;
    slug: string;
    axis: string;
    syswebId: string;
    syswebCode: string;
    syswebUrl: string;
    contactEmail: string;
    contactPhone: string;
    description: string;
    maxCapacity: number;
    attendeeRegStart: string;
    attendeeRegEnd: string;
    speakerBraceletEnabled: boolean;
    allowFreeOyenteUnach: boolean;
    eventModality: 'physical' | 'virtual';
    virtualZoomUrl: string;
    virtualDate: string;
    virtualTime: string;
    virtualTimezone: string;
    virtualPasscode: string;
    virtualScheduledSendAt: string;
}

interface EventsManagementViewProps {
    editions: Edition[];
    selectedEditionId: string;
    onEditionChange: (editionId: string) => void;
    onEditionCreated?: () => void;
}

type FilterStatus = 'all' | 'active' | 'inactive';

const ITEMS_PER_PAGE = 10;

// Axis badge colors
const AXIS_STYLES: Record<string, string> = {
    cientifico: 'bg-blue-100 text-blue-700 border-blue-200',
    cultural: 'bg-purple-100 text-purple-700 border-purple-200',
    innovacion: 'bg-yellow-100 text-yellow-700 border-yellow-200',
};

const AXIS_LABELS: Record<string, string> = {
    cientifico: 'Científico',
    cultural: 'Cultural',
    innovacion: 'Innovación',
};

const toDateTimeLocal = (value: string | null): string => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

const defaultEventFormValues: EventFormValues = {
    name: '',
    slug: '',
    axis: '',
    syswebId: '',
    syswebCode: '',
    syswebUrl: '',
    contactEmail: '',
    contactPhone: '',
    description: '',
    maxCapacity: 100,
    attendeeRegStart: '',
    attendeeRegEnd: '',
    speakerBraceletEnabled: false,
    allowFreeOyenteUnach: false,
    eventModality: 'physical',
    virtualZoomUrl: '',
    virtualDate: '',
    virtualTime: '',
    virtualTimezone: 'America/Mexico_City',
    virtualPasscode: '',
    virtualScheduledSendAt: '',
};

export function EventsManagementView({
    editions,
    selectedEditionId,
    onEditionChange,
    onEditionCreated,
}: EventsManagementViewProps) {
    const { user } = useAuth();
    // State
    const [events, setEvents] = useState<Event[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
    const [currentPage, setCurrentPage] = useState(1);

    // Sorting State
    const [sortColumn, setSortColumn] = useState<'name' | 'axis' | 'capacity' | 'status'>('name');
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

    // Modal State
    const [isEventModalOpen, setIsEventModalOpen] = useState(false);
    const [isEditionModalOpen, setIsEditionModalOpen] = useState(false);
    const [editingEvent, setEditingEvent] = useState<Event | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [virtualAccessDeliveredCount, setVirtualAccessDeliveredCount] = useState(0);
    const [editingEventIsVirtual, setEditingEventIsVirtual] = useState(false);
    const [subeventsEvent, setSubeventsEvent] = useState<Event | null>(null);

    const {
        register,
        handleSubmit,
        reset,
        setValue,
        watch,
    } = useForm<EventFormValues>({
        defaultValues: defaultEventFormValues,
    });

    const eventForm = watch();
    const canManageOyenteUnachSetting = Boolean(
        editingEvent && (
            user?.isSuperAdmin ||
            hasPermission(user?.permissionsByEvent?.[editingEvent.id], PERMISSIONS.MANAGE_SETTINGS)
        )
    );
    const canManageSubevents = (eventId: string) => Boolean(
        user?.isSuperAdmin ||
        hasPermission(user?.permissionsByEvent?.[eventId], PERMISSIONS.MANAGE_EVENTS)
    );

    // Edition Form State
    const [editionForm, setEditionForm] = useState({
        year: new Date().getFullYear() + 1,
        name: '',
        isActive: false,
    });

    // Fetch events when edition changes
    useEffect(() => {
        if (!selectedEditionId) return;

        const fetchEvents = async () => {
            setIsLoading(true);
            try {
                const { data } = await api.get(`/events/by-edition/${selectedEditionId}`);
                setEvents(data || []);
            } catch (error) {
                console.error('Error fetching events:', error);
                toast.error('Error al cargar eventos');
            } finally {
                setIsLoading(false);
            }
        };

        fetchEvents();
    }, [selectedEditionId]);

    // Filtered and sorted events
    const filteredEvents = useMemo(() => {
        let result = events.filter((event) => {
            // Search filter
            const matchesSearch =
                searchQuery === '' ||
                event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                event.slug.toLowerCase().includes(searchQuery.toLowerCase());

            // Status filter
            const matchesStatus =
                filterStatus === 'all' ||
                (filterStatus === 'active' && event.isActive) ||
                (filterStatus === 'inactive' && !event.isActive);

            return matchesSearch && matchesStatus;
        });

        // Sorting
        result.sort((a, b) => {
            let comparison = 0;
            switch (sortColumn) {
                case 'name':
                    comparison = a.name.localeCompare(b.name);
                    break;
                case 'axis':
                    comparison = (a.axis || '').localeCompare(b.axis || '');
                    break;
                case 'capacity':
                    comparison = a.currentCount - b.currentCount;
                    break;
                case 'status':
                    comparison = (a.isActive ? 1 : 0) - (b.isActive ? 1 : 0);
                    break;
            }
            return sortDirection === 'asc' ? comparison : -comparison;
        });

        return result;
    }, [events, searchQuery, filterStatus, sortColumn, sortDirection]);

    const dateValidationError = useMemo(() => {
        if (!eventForm.attendeeRegStart || !eventForm.attendeeRegEnd) return '';
        const start = new Date(eventForm.attendeeRegStart);
        const end = new Date(eventForm.attendeeRegEnd);
        if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
            return 'Formato de fecha inválido.';
        }
        if (start >= end) {
            return 'La fecha de fin debe ser posterior a la de inicio.';
        }
        return '';
    }, [eventForm.attendeeRegStart, eventForm.attendeeRegEnd]);

    const totalPages = Math.ceil(filteredEvents.length / ITEMS_PER_PAGE);
    const paginatedEvents = filteredEvents.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
    );

    // Reset page when filters or sorting change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchQuery, filterStatus, selectedEditionId, sortColumn, sortDirection]);

    // Handle sorting
    const handleSort = (column: 'name' | 'axis' | 'capacity' | 'status') => {
        if (sortColumn === column) {
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            setSortColumn(column);
            setSortDirection('asc');
        }
    };

    // Sort icon component
    const SortIcon = ({ column }: { column: 'name' | 'axis' | 'capacity' | 'status' }) => {
        if (sortColumn !== column) return <ArrowUpDown className="h-4 w-4 ml-1 text-gray-400" />;
        return sortDirection === 'asc'
            ? <ArrowUp className="h-4 w-4 ml-1 text-[#1e3a5f]" />
            : <ArrowDown className="h-4 w-4 ml-1 text-[#1e3a5f]" />;
    };

    // Check if registration is open
    const isRegistrationOpen = (event: Event): boolean => {
        if (!event.isActive) return false;
        const now = new Date();
        const start = event.attendeeRegStart ? new Date(event.attendeeRegStart) : null;
        const end = event.attendeeRegEnd ? new Date(event.attendeeRegEnd) : null;
        if (start && start > now) return false;
        if (end && end < now) return false;
        return true;
    };

    // Generate slug from name
    const generateSlug = (name: string): string => {
        return name
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '')
            .replace(/[^a-z0-9]+/g, '-')
            .replace(/^-+|-+$/g, '');
    };

    const handleEventFormChange = (field: keyof EventFormValues, value: string | number) => {
        setValue(field, value as never);
    };

    const resetVirtualAttendanceIntent = () => {
        setValue('eventModality', 'physical');
        setValue('virtualZoomUrl', '');
        setValue('virtualDate', '');
        setValue('virtualTime', '');
        setValue('virtualTimezone', 'America/Mexico_City');
        setValue('virtualPasscode', '');
    };

    const handleAxisChange = (value: string) => {
        handleEventFormChange('axis', value);
        if (!editingEvent && value !== 'cientifico') {
            resetVirtualAttendanceIntent();
        }
    };

    const handleEventModalityChange = (value: string) => {
        if (value === 'virtual') {
            setValue('eventModality', 'virtual');
            return;
        }
        resetVirtualAttendanceIntent();
    };

    useEffect(() => {
        if (!editingEvent) {
            setValue('slug', generateSlug(eventForm.name));
        }
    }, [eventForm.name, editingEvent, setValue]);

    // Open create event modal
    const openCreateEventModal = () => {
        setEditingEvent(null);
        setEditingEventIsVirtual(false);
        setVirtualAccessDeliveredCount(0);
        reset(defaultEventFormValues);
        setIsEventModalOpen(true);
    };

    // Open edit event modal
    const openEditEventModal = async (event: Event) => {
        setEditingEvent(event);
        try {
            const { data } = await api.get<EventAdminDetail>(`/events/admin/${event.id}`);
            let virtualConfig = {
                enabled: false,
                zoomUrl: '',
                date: '',
                time: '',
                timezone: 'America/Mexico_City',
                passcode: '',
                scheduledSendAt: '',
            };
            let deliveredCount = 0;
            let isVirtual = false;
            if (data.axis === 'cientifico') {
                const virtualResponse = await api.get(`/events/admin/${event.id}/virtual-attendance`);
                isVirtual = virtualResponse.data.isVirtual === true;
                if (isVirtual) {
                    virtualConfig = { ...virtualConfig, ...(virtualResponse.data.config || {}) };
                }
                deliveredCount = Number(virtualResponse.data.deliveredCount || 0);
            }
            setEditingEventIsVirtual(isVirtual);
            setVirtualAccessDeliveredCount(deliveredCount);
            reset({
                name: data.name,
                slug: data.slug,
                axis: data.axis || '',
                syswebId: data.syswebId || '',
                syswebCode: data.syswebCode || '',
                syswebUrl: data.syswebUrl || '',
                contactEmail: data.contactEmail || '',
                contactPhone: data.contactPhone || '',
                description: data.description || '',
                maxCapacity: data.maxCapacity,
                attendeeRegStart: toDateTimeLocal(data.attendeeRegStart),
                attendeeRegEnd: toDateTimeLocal(data.attendeeRegEnd),
                speakerBraceletEnabled: data.speakerBraceletEnabled || false,
                allowFreeOyenteUnach: data.allowFreeOyenteUnach === true,
                eventModality: isVirtual ? 'virtual' : 'physical',
                virtualZoomUrl: virtualConfig.zoomUrl,
                virtualDate: virtualConfig.date,
                virtualTime: virtualConfig.time,
                virtualTimezone: virtualConfig.timezone,
                virtualPasscode: virtualConfig.passcode,
                virtualScheduledSendAt: toDateTimeLocal(virtualConfig.scheduledSendAt),
            });
            setIsEventModalOpen(true);
        } catch (error) {
            console.error('Error fetching admin event detail:', error);
            toast.error('Error al cargar detalles del evento');
        }
    };

    // Save event (create or update)
    const handleSaveEvent = async (values: EventFormValues) => {
        if (!values.name || !values.slug) {
            toast.error('Nombre y slug son requeridos');
            return;
        }
        if (!values.attendeeRegStart || !values.attendeeRegEnd) {
            toast.error('Inicio y fin de registro público son requeridos');
            return;
        }
        if (dateValidationError) {
            toast.error(dateValidationError);
            return;
        }
        const shouldCreateVirtual = !editingEvent && values.axis === 'cientifico' && values.eventModality === 'virtual';
        const shouldUpdateVirtual = Boolean(editingEvent && editingEventIsVirtual);
        if (
            (shouldCreateVirtual || shouldUpdateVirtual) &&
            (!values.virtualZoomUrl || !values.virtualDate || !values.virtualTime || !values.virtualTimezone || !values.virtualScheduledSendAt)
        ) {
            toast.error('Completa URL, fecha, hora, zona horaria y fecha de envío para habilitar la modalidad virtual.');
            return;
        }

        setIsSaving(true);
        try {
            if (editingEvent) {
                // Update
                await api.put(`/events/${editingEvent.id}`, {
                    name: values.name,
                    description: values.description || undefined,
                    axis: values.axis || undefined,
                    syswebId: values.syswebId || null,
                    syswebCode: values.syswebCode || null,
                    syswebUrl: values.syswebUrl || null,
                    contactEmail: values.contactEmail || null,
                    contactPhone: values.contactPhone || null,
                    maxCapacity: values.maxCapacity,
                    attendeeRegStart: new Date(values.attendeeRegStart).toISOString(),
                    attendeeRegEnd: new Date(values.attendeeRegEnd).toISOString(),
                    speakerBraceletEnabled: values.speakerBraceletEnabled,
                });
                if (canManageOyenteUnachSetting) {
                    await settingsApi.updateSetting(ALLOW_FREE_OYENTE_UNACH_SETTING_KEY, {
                        scope: 'EVENT',
                        eventId: editingEvent.id,
                        value: values.allowFreeOyenteUnach,
                        type: 'BOOLEAN',
                        description: 'Permitir registro gratuito Oyente UNACH',
                    });
                }
                if (shouldUpdateVirtual) {
                    await api.put(`/events/admin/${editingEvent.id}/virtual-attendance`, {
                        zoomUrl: values.virtualZoomUrl,
                        date: values.virtualDate,
                        time: values.virtualTime,
                        timezone: values.virtualTimezone,
                        passcode: values.virtualPasscode,
                        scheduledSendAt: new Date(values.virtualScheduledSendAt).toISOString(),
                    });
                }
                toast.success('Evento actualizado');
            } else {
                // Create
                await api.post('/events', {
                    editionId: selectedEditionId,
                    name: values.name,
                    slug: values.slug,
                    description: values.description || undefined,
                    axis: values.axis || undefined,
                    syswebId: values.syswebId || null,
                    syswebCode: values.syswebCode || null,
                    syswebUrl: values.syswebUrl || null,
                    contactEmail: values.contactEmail || null,
                    contactPhone: values.contactPhone || null,
                    maxCapacity: values.maxCapacity,
                    attendeeRegStart: new Date(values.attendeeRegStart).toISOString(),
                    attendeeRegEnd: new Date(values.attendeeRegEnd).toISOString(),
                    speakerBraceletEnabled: values.speakerBraceletEnabled,
                    ...(shouldCreateVirtual ? {
                        virtualAttendance: {
                            zoomUrl: values.virtualZoomUrl,
                            date: values.virtualDate,
                            time: values.virtualTime,
                            timezone: values.virtualTimezone,
                            passcode: values.virtualPasscode,
                            scheduledSendAt: new Date(values.virtualScheduledSendAt).toISOString(),
                        },
                    } : {}),
                });
                toast.success('Evento creado');
            }

            // Reload events
            const { data } = await api.get(`/events/by-edition/${selectedEditionId}`);
            setEvents(data || []);
            setIsEventModalOpen(false);
        } catch (error: any) {
            const message = error.response?.data?.message || 'Error al guardar evento';
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    // Toggle event active status
    const handleToggleEventStatus = async (event: Event) => {
        try {
            await api.put(`/events/${event.id}`, { isActive: !event.isActive });
            setEvents((prev) =>
                prev.map((e) => (e.id === event.id ? { ...e, isActive: !e.isActive } : e))
            );
            toast.success(event.isActive ? 'Evento desactivado' : 'Evento activado');
        } catch (error: any) {
            console.error('Toggle event error:', error.response?.data || error);
            const message = error.response?.data?.message || 'Error al cambiar estado';
            toast.error(message);
        }
    };

    // Create edition
    const handleCreateEdition = async () => {
        if (!editionForm.year) {
            toast.error('El año es requerido');
            return;
        }

        setIsSaving(true);
        try {
            await api.post('/editions', editionForm);
            toast.success('Edición creada');
            setIsEditionModalOpen(false);
            onEditionCreated?.();
        } catch (error: any) {
            const message = error.response?.data?.message || 'Error al crear edición';
            toast.error(message);
        } finally {
            setIsSaving(false);
        }
    };

    return (
        <div className="space-y-6 animate-in fade-in-50 duration-300">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0f2744]">Gestión de Eventos</h2>
                    <p className="text-sm text-gray-500 mt-1">
                        Administra los eventos de la edición seleccionada
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button
                        onClick={openCreateEventModal}
                        className="bg-teal-600 hover:bg-teal-700 text-white"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Nuevo Evento
                    </Button>
                    <Button
                        onClick={() => {
                            setEditionForm({ year: new Date().getFullYear() + 1, name: '', isActive: false });
                            setIsEditionModalOpen(true);
                        }}
                        className="bg-[#1e3a5f] hover:bg-[#0f2744] text-white"
                    >
                        <Calendar className="h-4 w-4 mr-2" />
                        Nueva Edición
                    </Button>
                </div>
            </div>

            {/* Filters */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center bg-white p-4 rounded-lg border border-gray-200">
                {/* Year Selector */}
                <div className="flex items-center gap-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                        Año:
                    </Label>
                    <Select value={selectedEditionId} onValueChange={onEditionChange}>
                        <SelectTrigger className="w-[120px] bg-gray-50">
                            <SelectValue placeholder="Año" />
                        </SelectTrigger>
                        <SelectContent>
                            {editions.map((ed) => (
                                <SelectItem key={ed.id} value={ed.id}>
                                    {ed.year} {ed.isActive && '✓'}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {/* Search */}
                <div className="relative flex-1 min-w-[200px]">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <Input
                        placeholder="Buscar eventos..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="pl-9 bg-gray-50"
                    />
                </div>

                {/* Status Filter */}
                <div className="flex items-center gap-2">
                    <Label className="text-xs font-semibold text-gray-500 uppercase whitespace-nowrap">
                        Estados:
                    </Label>
                    <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                        {(['all', 'active', 'inactive'] as FilterStatus[]).map((status) => (
                            <button
                                key={status}
                                onClick={() => setFilterStatus(status)}
                                className={`px-3 py-1.5 text-sm font-medium transition-colors ${filterStatus === status
                                    ? 'bg-[#1e3a5f] text-white'
                                    : 'bg-white text-gray-600 hover:bg-gray-50'
                                    }`}
                            >
                                {status === 'all' ? 'Todos' : status === 'active' ? 'Activos' : 'Inactivos'}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                {isLoading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-[#1e3a5f]" />
                    </div>
                ) : paginatedEvents.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <Calendar className="h-12 w-12 text-gray-300 mb-4" />
                        <h3 className="text-lg font-medium text-gray-900">No hay eventos</h3>
                        <p className="text-sm text-gray-500 mt-1">
                            {searchQuery || filterStatus !== 'all'
                                ? 'No se encontraron eventos con los filtros aplicados'
                                : 'Crea el primer evento de esta edición'}
                        </p>
                    </div>
                ) : (
                    <Table>
                        <TableHeader>
                            <TableRow className="bg-gray-50">
                                <TableHead
                                    className="font-semibold cursor-pointer hover:text-[#1e3a5f] transition-colors"
                                    onClick={() => handleSort('name')}
                                >
                                    <div className="flex items-center">
                                        Nombre del Evento
                                        <SortIcon column="name" />
                                    </div>
                                </TableHead>
                                <TableHead
                                    className="font-semibold cursor-pointer hover:text-[#1e3a5f] transition-colors"
                                    onClick={() => handleSort('axis')}
                                >
                                    <div className="flex items-center">
                                        Eje
                                        <SortIcon column="axis" />
                                    </div>
                                </TableHead>
                                <TableHead
                                    className="font-semibold cursor-pointer hover:text-[#1e3a5f] transition-colors"
                                    onClick={() => handleSort('capacity')}
                                >
                                    <div className="flex items-center">
                                        Capacidad
                                        <SortIcon column="capacity" />
                                    </div>
                                </TableHead>
                                <TableHead
                                    className="font-semibold cursor-pointer hover:text-[#1e3a5f] transition-colors"
                                    onClick={() => handleSort('status')}
                                >
                                    <div className="flex items-center">
                                        Registro
                                        <SortIcon column="status" />
                                    </div>
                                </TableHead>
                                <TableHead className="font-semibold text-right">Acciones</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {paginatedEvents.map((event) => {
                                const capacityPercent = (event.currentCount / event.maxCapacity) * 100;
                                const regOpen = isRegistrationOpen(event);

                                return (
                                    <TableRow key={event.id} className="hover:bg-gray-50/50">
                                        <TableCell>
                                            <div>
                                                <p className="font-medium text-gray-900">{event.name}</p>
                                                <p className="text-xs text-gray-500">{event.slug}</p>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            {event.axis ? (
                                                <Badge
                                                    variant="outline"
                                                    className={AXIS_STYLES[event.axis] || 'bg-gray-100 text-gray-700'}
                                                >
                                                    {AXIS_LABELS[event.axis] || event.axis}
                                                </Badge>
                                            ) : (
                                                <span className="text-gray-400 text-sm">—</span>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <div className="w-32">
                                                <div className="flex justify-between text-xs mb-1">
                                                    <span className="text-gray-600">{event.currentCount}</span>
                                                    <span className="text-gray-400">/{event.maxCapacity}</span>
                                                </div>
                                                <Progress
                                                    value={capacityPercent}
                                                    className="h-2"
                                                />
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Badge
                                                variant="outline"
                                                className={
                                                    regOpen
                                                        ? 'bg-green-100 text-green-700 border-green-200'
                                                        : 'bg-red-100 text-red-700 border-red-200'
                                                }
                                            >
                                                {regOpen ? 'Abierto' : 'Cerrado'}
                                            </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-1">
                                                {canManageSubevents(event.id) && (
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        onClick={() => setSubeventsEvent(event)}
                                                        className="h-8 w-8 text-gray-500 hover:text-[#1e3a5f]"
                                                        title="Gestionar subeventos"
                                                        aria-label={`Gestionar subeventos de ${event.name}`}
                                                    >
                                                        <ListTree className="h-4 w-4" />
                                                    </Button>
                                                )}
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => openEditEventModal(event)}
                                                    className="h-8 w-8 text-gray-500 hover:text-[#1e3a5f]"
                                                    title="Editar"
                                                >
                                                    <Pencil className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                    variant="ghost"
                                                    size="icon"
                                                    onClick={() => handleToggleEventStatus(event)}
                                                    className={`h-8 w-8 ${event.isActive
                                                        ? 'text-gray-500 hover:text-red-600'
                                                        : 'text-gray-400 hover:text-green-600'
                                                        }`}
                                                    title={event.isActive ? 'Desactivar' : 'Activar'}
                                                >
                                                    <Power className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                )}

                {/* Pagination */}
                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200 bg-gray-50">
                        <p className="text-sm text-gray-600">
                            Mostrando {(currentPage - 1) * ITEMS_PER_PAGE + 1}-
                            {Math.min(currentPage * ITEMS_PER_PAGE, filteredEvents.length)} de{' '}
                            {filteredEvents.length} eventos
                        </p>
                        <div className="flex items-center gap-1">
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                                className="h-8 w-8"
                            >
                                <ChevronLeft className="h-4 w-4" />
                            </Button>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                                <Button
                                    key={page}
                                    variant={page === currentPage ? 'default' : 'outline'}
                                    size="icon"
                                    onClick={() => setCurrentPage(page)}
                                    className={`h-8 w-8 ${page === currentPage ? 'bg-[#1e3a5f]' : ''
                                        }`}
                                >
                                    {page}
                                </Button>
                            ))}
                            <Button
                                variant="outline"
                                size="icon"
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage === totalPages}
                                className="h-8 w-8"
                            >
                                <ChevronRight className="h-4 w-4" />
                            </Button>
                        </div>
                    </div>
                )}
            </div>

            {/* Event Modal */}
            <Dialog open={isEventModalOpen} onOpenChange={setIsEventModalOpen}>
                <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-hidden">
                    <DialogHeader>
                        <DialogTitle>
                            {editingEvent ? 'Editar Evento' : 'Nuevo Evento'}
                        </DialogTitle>
                        <DialogDescription>
                            {editingEvent
                                ? 'Modifica los datos del evento'
                                : 'Crea un nuevo evento para esta edición'}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4 overflow-y-auto pr-1 max-h-[calc(90vh-12rem)]">
                        <div className="grid gap-2">
                            <Label htmlFor="name">Nombre del Evento *</Label>
                            <Input
                                id="name"
                                {...register('name')}
                                placeholder="Ej: VII Congreso Internacional de Ciencia"
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="slug">Slug (URL) *</Label>
                            <Input
                                id="slug"
                                {...register('slug')}
                                placeholder="vii-congreso-ciencia-2026"
                                disabled={!!editingEvent}
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="axis">Eje Temático</Label>
                            <Select
                                value={eventForm.axis}
                                onValueChange={handleAxisChange}
                                disabled={Boolean(editingEvent && editingEventIsVirtual)}
                            >
                                <SelectTrigger>
                                    <SelectValue placeholder="Seleccionar eje" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="cientifico">Científico</SelectItem>
                                    <SelectItem value="cultural">Cultural</SelectItem>
                                    <SelectItem value="innovacion">Innovación</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="description">Descripción</Label>
                            <Textarea
                                id="description"
                                {...register('description')}
                                placeholder="Descripción del evento..."
                                rows={3}
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="maxCapacity">Capacidad Máxima</Label>
                            <Input
                                id="maxCapacity"
                                type="number"
                                {...register('maxCapacity', { valueAsNumber: true })}
                            />
                        </div>

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="grid gap-2">
                                <Label htmlFor="attendeeRegStart">Inicio de Registro Público *</Label>
                                <Input
                                    id="attendeeRegStart"
                                    type="datetime-local"
                                    {...register('attendeeRegStart')}
                                    required
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="attendeeRegEnd">Fin de Registro Público *</Label>
                                <Input
                                    id="attendeeRegEnd"
                                    type="datetime-local"
                                    {...register('attendeeRegEnd')}
                                    required
                                />
                            </div>
                        </div>

                        {((!editingEvent && eventForm.axis === 'cientifico') || editingEventIsVirtual) && (
                            <div className="space-y-4 rounded-lg border border-violet-200 bg-violet-50/60 p-4">
                                {editingEventIsVirtual ? (
                                    <div>
                                        <Label className="font-semibold text-violet-950">Modalidad: Virtual</Label>
                                        <p className="mt-1 text-xs text-violet-800">
                                            La modalidad es inmutable. Puedes actualizar únicamente la configuración de acceso por Zoom.
                                        </p>
                                    </div>
                                ) : (
                                    <div className="space-y-3">
                                        <Label className="font-semibold text-violet-950">Modalidad del evento *</Label>
                                        <RadioGroup
                                            value={eventForm.eventModality}
                                            onValueChange={handleEventModalityChange}
                                            className="flex flex-wrap gap-5"
                                            aria-label="Modalidad del evento"
                                        >
                                            <div className="flex items-center gap-2">
                                                <RadioGroupItem value="physical" id="event-modality-physical" />
                                                <Label htmlFor="event-modality-physical" className="font-normal">Presencial</Label>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <RadioGroupItem value="virtual" id="event-modality-virtual" />
                                                <Label htmlFor="event-modality-virtual" className="font-normal">Virtual</Label>
                                            </div>
                                        </RadioGroup>
                                    </div>
                                )}

                                {virtualAccessDeliveredCount > 0 && (
                                    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
                                        Ya se enviaron {virtualAccessDeliveredCount} accesos virtuales. Guardar cambios no enviará correos masivos; cualquier actualización deberá notificarse por los canales institucionales o mediante reenvío manual.
                                    </div>
                                )}

                                {(editingEventIsVirtual || eventForm.eventModality === 'virtual') && (
                                <div className="grid gap-4 sm:grid-cols-2">
                                    <div className="grid gap-2 sm:col-span-2">
                                        <Label htmlFor="virtualZoomUrl">URL de Zoom *</Label>
                                        <Input
                                            id="virtualZoomUrl"
                                            type="url"
                                            {...register('virtualZoomUrl')}
                                            placeholder="https://unach.zoom.us/j/..."
                                        />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label htmlFor="virtualDate">Fecha *</Label>
                                        <Input id="virtualDate" type="date" {...register('virtualDate')} />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label htmlFor="virtualTime">Hora *</Label>
                                        <Input id="virtualTime" type="time" {...register('virtualTime')} />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label htmlFor="virtualTimezone">Zona horaria *</Label>
                                        <Input id="virtualTimezone" {...register('virtualTimezone')} />
                                    </div>
                                    <div className="grid gap-2">
                                        <Label htmlFor="virtualPasscode">Código de acceso (opcional)</Label>
                                        <Input id="virtualPasscode" {...register('virtualPasscode')} autoComplete="off" />
                                    </div>
                                    <div className="grid gap-2 sm:col-span-2">
                                        <Label htmlFor="virtualScheduledSendAt">Fecha y hora de envío del acceso Zoom *</Label>
                                        <Input
                                            id="virtualScheduledSendAt"
                                            type="datetime-local"
                                            {...register('virtualScheduledSendAt')}
                                        />
                                        <p className="text-xs text-violet-800">
                                            El acceso se enviará a partir de este instante, usando la zona horaria del navegador del administrador.
                                        </p>
                                    </div>
                                </div>
                                )}
                            </div>
                        )}

                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="grid gap-2">
                                <Label htmlFor="syswebId">CVE Sysweb</Label>
                                <Input
                                    id="syswebId"
                                    {...register('syswebId')}
                                    placeholder="Solo digitos"
                                />
                            </div>
                            <div className="grid gap-2">
                                <Label htmlFor="syswebCode">Codigo Sysweb</Label>
                                <Input
                                    id="syswebCode"
                                    {...register('syswebCode')}
                                    placeholder="Codigo alfanumerico"
                                />
                            </div>
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="syswebUrl">URL Sysweb</Label>
                            <Input
                                id="syswebUrl"
                                {...register('syswebUrl')}
                                placeholder="https://sysweb.unach.mx/..."
                            />
                        </div>

                        <div className="rounded-lg border border-blue-100 bg-blue-50/70 p-4">
                            <div className="flex items-start justify-between gap-4">
                                <div className="min-w-0 space-y-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Label htmlFor="speakerBraceletEnabled" className="text-sm font-semibold leading-none text-[#0f2744]">
                                            Codigo de pulsera para ponentes
                                        </Label>
                                        <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-none ${
                                            eventForm.speakerBraceletEnabled
                                                ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                                : 'border-slate-200 bg-white text-slate-500'
                                        }`}>
                                            {eventForm.speakerBraceletEnabled ? 'Habilitado' : 'Deshabilitado'}
                                        </span>
                                    </div>
                                    <p className="max-w-[26rem] text-xs leading-relaxed text-slate-600">
                                        Permite capturar una pulsera de pago anticipado. La conciliacion ocurre solo despues de la aprobacion academica.
                                    </p>
                                </div>
                                <Switch
                                    id="speakerBraceletEnabled"
                                    aria-label="Habilitar codigo de pulsera para ponentes"
                                    checked={Boolean(eventForm.speakerBraceletEnabled)}
                                    onCheckedChange={(checked) => setValue('speakerBraceletEnabled', checked)}
                                    className="mt-0.5 !h-8 !w-14 !rounded-full !border !border-slate-300 data-[state=checked]:!bg-[#1e3a5f] data-[state=unchecked]:!bg-slate-300 [&_[data-slot=switch-thumb]]:!size-6 [&_[data-slot=switch-thumb]]:!rounded-full [&_[data-slot=switch-thumb]]:!bg-white [&_[data-slot=switch-thumb]]:shadow-sm [&_[data-slot=switch-thumb][data-state=unchecked]]:!translate-x-[2px] [&_[data-slot=switch-thumb][data-state=checked]]:!translate-x-[30px]"
                                />
                            </div>
                        </div>

                        {editingEvent && canManageOyenteUnachSetting && (
                            <div className="rounded-lg border border-amber-100 bg-amber-50/70 p-4">
                                <div className="flex items-start justify-between gap-4">
                                    <div className="min-w-0 space-y-2">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Label htmlFor="allowFreeOyenteUnach" className="text-sm font-semibold leading-none text-[#0f2744]">
                                                Permitir registro gratuito Oyente UNACH
                                            </Label>
                                            <span className={`rounded-full border px-2 py-0.5 text-[11px] font-semibold leading-none ${
                                                eventForm.allowFreeOyenteUnach
                                                    ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
                                                    : 'border-slate-200 bg-white text-slate-500'
                                            }`}>
                                                {eventForm.allowFreeOyenteUnach ? 'Habilitado' : 'Deshabilitado'}
                                            </span>
                                        </div>
                                        <p className="max-w-[26rem] text-xs leading-relaxed text-slate-600">
                                            Habilita nuevas altas gratuitas sin kit ni constancia para estudiantes validados por SIAE.
                                        </p>
                                    </div>
                                    <Switch
                                        id="allowFreeOyenteUnach"
                                        aria-label="Permitir registro gratuito Oyente UNACH"
                                        checked={Boolean(eventForm.allowFreeOyenteUnach)}
                                        onCheckedChange={(checked) => setValue('allowFreeOyenteUnach', checked)}
                                        className="mt-0.5 !h-8 !w-14 !rounded-full !border !border-slate-300 data-[state=checked]:!bg-[#1e3a5f] data-[state=unchecked]:!bg-slate-300 [&_[data-slot=switch-thumb]]:!size-6 [&_[data-slot=switch-thumb]]:!rounded-full [&_[data-slot=switch-thumb]]:!bg-white [&_[data-slot=switch-thumb]]:shadow-sm [&_[data-slot=switch-thumb][data-state=unchecked]]:!translate-x-[2px] [&_[data-slot=switch-thumb][data-state=checked]]:!translate-x-[30px]"
                                    />
                                </div>
                            </div>
                        )}

                        <div className="grid gap-3 rounded-lg border border-gray-200 p-4">
                            <div>
                                <h4 className="text-sm font-semibold text-gray-900">Contacto del evento</h4>
                                <p className="text-xs text-gray-500">Datos opcionales de soporte para este evento.</p>
                            </div>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div className="grid gap-2">
                                    <Label htmlFor="contactEmail">Correo de contacto</Label>
                                    <Input
                                        id="contactEmail"
                                        type="email"
                                        {...register('contactEmail')}
                                        placeholder="contacto@unach.mx"
                                    />
                                </div>
                                <div className="grid gap-2">
                                    <Label htmlFor="contactPhone">Teléfono de contacto</Label>
                                    <Input
                                        id="contactPhone"
                                        {...register('contactPhone')}
                                        placeholder="+52 961 000 0000"
                                    />
                                </div>
                            </div>
                        </div>

                        {dateValidationError && (
                            <p className="text-sm text-red-600">{dateValidationError}</p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEventModalOpen(false)}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleSubmit(handleSaveEvent)}
                            disabled={isSaving}
                            className="bg-[#1e3a5f] hover:bg-[#0f2744] !text-white"
                        >
                            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            {editingEvent ? 'Guardar Cambios' : 'Crear Evento'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <SubeventsManager
                event={subeventsEvent ? { id: subeventsEvent.id, name: subeventsEvent.name } : null}
                open={Boolean(subeventsEvent)}
                onOpenChange={(open) => {
                    if (!open) setSubeventsEvent(null);
                }}
            />

            {/* Edition Modal */}
            <Dialog open={isEditionModalOpen} onOpenChange={setIsEditionModalOpen}>
                <DialogContent className="sm:max-w-[400px]">
                    <DialogHeader>
                        <DialogTitle>Nueva Edición</DialogTitle>
                        <DialogDescription>
                            Crea una nueva edición anual para organizar eventos
                        </DialogDescription>
                    </DialogHeader>

                    <div className="grid gap-4 py-4">
                        <div className="grid gap-2">
                            <Label htmlFor="year">Año *</Label>
                            <Input
                                id="year"
                                type="number"
                                value={editionForm.year}
                                onChange={(e) =>
                                    setEditionForm((prev) => ({ ...prev, year: parseInt(e.target.value) || 0 }))
                                }
                            />
                        </div>

                        <div className="grid gap-2">
                            <Label htmlFor="editionName">Nombre (opcional)</Label>
                            <Input
                                id="editionName"
                                value={editionForm.name}
                                onChange={(e) =>
                                    setEditionForm((prev) => ({ ...prev, name: e.target.value }))
                                }
                                placeholder="Ej: Edición Especial Aniversario"
                            />
                        </div>

                        <div className="flex items-center gap-2">
                            <input
                                type="checkbox"
                                id="isActive"
                                checked={editionForm.isActive}
                                onChange={(e) =>
                                    setEditionForm((prev) => ({ ...prev, isActive: e.target.checked }))
                                }
                                className="rounded border-gray-300"
                            />
                            <Label htmlFor="isActive" className="text-sm font-normal">
                                Marcar como edición activa
                            </Label>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditionModalOpen(false)}>
                            Cancelar
                        </Button>
                        <Button
                            onClick={handleCreateEdition}
                            disabled={isSaving}
                            className="bg-[#1e3a5f] hover:bg-[#0f2744]"
                        >
                            {isSaving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                            Crear Edición
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

